import React, { useEffect, useMemo, useState } from "react";

// Gate C5A — the one countdown used by both the customer checkout view
// and the tenant/Kitchen card.
//
// The rule that matters: the SERVER decides. This component is handed an
// absolute ISO deadline that the backend computed and stored, and it only
// renders the difference. It never derives a deadline from a duration
// ("3 minutes from when this mounted"), because a tab left in the
// background, a suspended laptop or a wrong system clock would then show
// a customer time they do not have. When the countdown reaches zero the
// UI disables its action, but the real gate is the server re-reading the
// same stored timestamp on the next request — a stale UI can look wrong;
// it can never authorize a booking.
//
// Accessibility (Gate C5A §7): urgency is carried by TEXT (the state word
// and the remaining time), by an icon glyph, and by `role="status"` with
// `aria-live` — colour is added on top of those, never the only signal.

export const URGENCY_UNKNOWN = "unknown";
export const URGENCY_NORMAL = "normal";
export const URGENCY_WARNING = "warning";
export const URGENCY_URGENT = "urgent";
export const URGENCY_EXPIRED = "expired";

const WARNING_SECONDS = 5 * 60;
const URGENT_SECONDS = 2 * 60;

export function urgencyForSeconds(remaining) {
  if (remaining === null || remaining === undefined) return URGENCY_UNKNOWN;
  if (remaining <= 0) return URGENCY_EXPIRED;
  if (remaining <= URGENT_SECONDS) return URGENCY_URGENT;
  if (remaining <= WARNING_SECONDS) return URGENCY_WARNING;
  return URGENCY_NORMAL;
}

export function formatRemaining(remaining) {
  if (remaining === null || remaining === undefined) return "—";
  if (remaining <= 0) return "0:00";
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

// Text that stands alone without colour. Screen readers and monochrome
// displays get the same information a colour-sighted user does.
const URGENCY_TEXT = {
  [URGENCY_NORMAL]: "On time",
  [URGENCY_WARNING]: "Warning — 5 minutes or less",
  [URGENCY_URGENT]: "Urgent — 2 minutes or less",
  [URGENCY_EXPIRED]: "Expired",
  [URGENCY_UNKNOWN]: "Time remaining unknown",
};

const URGENCY_ICON = {
  [URGENCY_NORMAL]: "●",
  [URGENCY_WARNING]: "▲",
  [URGENCY_URGENT]: "■",
  [URGENCY_EXPIRED]: "✕",
  [URGENCY_UNKNOWN]: "?",
};

const URGENCY_CLASS = {
  [URGENCY_NORMAL]: "text-emerald-700 bg-emerald-50 border-emerald-200",
  [URGENCY_WARNING]: "text-amber-800 bg-amber-50 border-amber-300",
  [URGENCY_URGENT]: "text-red-800 bg-red-50 border-red-300",
  [URGENCY_EXPIRED]: "text-stone-700 bg-stone-100 border-stone-300",
  [URGENCY_UNKNOWN]: "text-stone-700 bg-stone-100 border-stone-300",
};

export function secondsUntil(deadlineIso, nowMs) {
  if (!deadlineIso) return null;
  const parsed = Date.parse(deadlineIso);
  if (Number.isNaN(parsed)) return null;
  return Math.max(Math.floor((parsed - nowMs) / 1000), 0);
}

export default function QuoteCountdown({
  deadline,
  label,
  testId = "quote-countdown",
  onExpire,
}) {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    // One-second tick. Re-reading `Date.now()` each tick (rather than
    // decrementing a stored counter) means a throttled background tab
    // catches up to the true remaining time on its next tick instead of
    // drifting further behind with every missed interval.
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const remaining = useMemo(() => secondsUntil(deadline, nowMs), [deadline, nowMs]);
  const urgency = urgencyForSeconds(remaining);

  useEffect(() => {
    if (urgency === URGENCY_EXPIRED && typeof onExpire === "function") onExpire();
  }, [urgency, onExpire]);

  return (
    <div
      data-testid={testId}
      data-urgency={urgency}
      role="status"
      // `assertive` only once it is genuinely urgent: a polite live
      // region every second would make the rest of the page unusable
      // with a screen reader.
      aria-live={urgency === URGENCY_URGENT || urgency === URGENCY_EXPIRED ? "assertive" : "polite"}
      className={`inline-flex items-center gap-2 rounded-lg border px-2.5 py-1 text-sm font-semibold ${URGENCY_CLASS[urgency]}`}
    >
      <span aria-hidden="true">{URGENCY_ICON[urgency]}</span>
      {label ? <span className="font-medium">{label}</span> : null}
      <span data-testid={`${testId}-value`}>{formatRemaining(remaining)}</span>
      {/* The urgency word itself, visually hidden but always present in
          the accessibility tree and in the DOM — this is what makes the
          state independent of colour. */}
      <span className="sr-only" data-testid={`${testId}-urgency-text`}>
        {URGENCY_TEXT[urgency]}
      </span>
    </div>
  );
}
